<template>
  <div class="sb-navigation">
    <div class="sb-navigation-scrollable position-relative py-3">
      <div class="navigation-group" v-for="(nav, nav_idx) in disclosures"
        :key="`navigation-group-${nav_idx}-${nav.link}`">
        <template v-if="nav.type == 'title'">
          <div v-if="nav.title_lv === 1"
            :class="['d-flex align-items-center custom-padding-left', { 'mt-3': nav_idx !== 0 }, { 'eng-spacing': lang === 'ENG' }]">
            <h5 class="nav-title nav-title--parent" :class="{ 'eng-font': lang === 'ENG' }" :data-observe-anchor="'title_' + nav.link">
              <b-link class="nav-anchor text-keep-all text-decoration-none" @click="scroll_to_title(nav.link)">
                <span v-show="lang === 'KOR'">{{ nav.title1 }}</span>
                <span v-show="lang === 'ENG'">{{
                  nav.title1_em ? nav.title1_em : nav.title1_ea
                }}</span>
              </b-link>
            </h5>
          </div>
          <div v-if="nav.title_lv === 2" :class="{ 'eng-spacing': lang === 'ENG' }">
            <h6 :class="['nav-title nav-title--child', 'mb-0', 'custom-padding-left', { 'eng-font': lang === 'ENG' }]"
              :data-observe-anchor="'title_' + nav.link">
              <b-link class="nav-anchor text-keep-all text-decoration-none" @click="scroll_to_title(nav.link)">
                <span v-show="lang === 'KOR'">{{ nav.title1 }}</span>
                <span v-show="lang === 'ENG'">{{
                  nav.title1_em ? nav.title1_em : nav.title1_ea
                }}</span>
              </b-link>
            </h6>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import colorMixin from '../../../mixins/colorMixin';

export default {
  name: 'StoryBoardTocViewer',
  mixins: [colorMixin],
  props: {
    disclosures: { type: Array, default: null },
  },
  computed: {
    ...mapState('report_list', ['lang']),
    ...mapGetters('story_board', ['selected_lv1', 'selected_lv2']),
    ...mapGetters('report_list', ['selected_report']),
    anchorList() {
      if (!this.disclosures) return [];
      return this.disclosures.reduce((_anchors, navEle) => {
        if (navEle?.title_lv < 3) {
          _anchors.push(`#title_${navEle.link}`);
        }
        return _anchors;
      }, []);
    },
    indicatorColor() {
      return this.main_color || '#86D3FF';
    },
  },
  mounted() {
    window.addEventListener('scroll', this.handleWindowScroll);
    this.initializeIndicator();
  },
  beforeDestroy() {
    if (this.anchorObserver) {
      this.anchorObserver.disconnect();
      this.anchorObserver = null;
    }
    window.removeEventListener('scroll', this.handleWindowScroll);
  },
  data() {
    return {
      activatedAnchor: null,
      anchorObserver: null,
      observeDealyTimeSleep: 0,
    };
  },
  methods: {
    initializeIndicator() {
      // DOM이 업데이트된 후에 실행되도록 보장합니다.
      this.$nextTick(() => {
        // DOM에서 첫 번째 .nav-title 요소를 찾습니다.
        const firstNavElement = this.$el.querySelector('.nav-title');
        if (firstNavElement) {
          // 해당 요소가 가리키는 앵커 ID를 가져옵니다.
          const anchorId = firstNavElement.dataset.observeAnchor;
          // 인디케이터를 설정하는 메서드를 호출합니다.
          this.setActiveIndicator(anchorId);
        }
      });
    },
    setActiveIndicator(anchorId) {
      if (!anchorId || this.activatedAnchor === anchorId) return;

      const _activedNavs = document.querySelectorAll('.navigation-indicator');
      if (_activedNavs.length) {
        _activedNavs.forEach((_activedNav) => {
          _activedNav.classList.remove('navigation-indicator');
          _activedNav.style.color = '';
          _activedNav.style.borderLeft = '';
        });
      }

      const _navToAnchor = document.querySelector(`[data-observe-anchor="${anchorId}"]`);
      if (_navToAnchor) {
        this.activatedAnchor = anchorId;
        const color = this.indicatorColor;
        if (color) {
          const node = _navToAnchor.childNodes[0];
          node.classList.add('navigation-indicator');
          node.style.color = color;
          node.style.borderLeft = `4px solid ${color}`;
        }
      }
    },

    handlerObserveAnchor(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const anchorId = entry.target.id.replace(/#/g, '');
          this.setActiveIndicator(anchorId);
        }
      });
    },

    // --- 수정된 부분: 윈도우 스크롤 핸들러에 최상단 감지 로직 추가 ---
    handleWindowScroll() {
      if (!this.anchorList || this.anchorList.length === 0) return;

      const atTop = window.scrollY === 0;
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 1;

      if (atTop) {
        // anchorList에서 첫 번째 요소의 ID를 가져옵니다.
        const firstAnchor = this.anchorList[0];
        const firstAnchorId = firstAnchor.replace('#', '');

        // 첫 번째 요소에 인디케이터를 활성화합니다.
        this.setActiveIndicator(firstAnchorId);

      } else if (atBottom) {
        // anchorList에서 마지막 요소의 ID를 가져옵니다.
        const lastAnchor = this.anchorList[this.anchorList.length - 1];
        const lastAnchorId = lastAnchor.replace('#', '');

        // 마지막 요소에 인디케이터를 활성화합니다.
        this.setActiveIndicator(lastAnchorId);
      }
    },

    handle_toc_scroll() {
      if (!this.anchorList) return;
      this.observeDealyTimeSleep = 1000;

      setTimeout(() => {
        this.anchorObserver = new IntersectionObserver(this.handlerObserveAnchor, {
          rootMargin: '0% 0% -78.5% 0%',
          threshold: 1,
        });
        this.anchorList.forEach((_anchor) => {
          const _anchorTitle = document.querySelector(_anchor);
          if (_anchorTitle) {
            this.anchorObserver.observe(_anchorTitle);
          }
        });
        const initialNav = document.querySelector('.navigation-indicator');
        const color = this.indicatorColor;
        if (initialNav && color) {
          initialNav.style.color = color;
          initialNav.style.borderLeft = `4px solid ${color}`;
        }
      }, this.observeDealyTimeSleep);
    },
    scroll_to_title(link) {
      const element = document.getElementById('title_' + link);
      if (element) {
        element.style.scrollMarginTop = '100px';
        element.scrollIntoView({ behavior: 'auto' });
        history.pushState(null, null, '#title_' + link);
      }
    },
  },
  watch: {
    disclosures_loaded: {
      immediate: true,
      handler(newV) {
        if (newV) {
          setTimeout(() => {
            this.handle_toc_scroll();
          }, 1000)
        }
      },
    },
    disclosures: {
      immediate: true,
      handler(newV) {
        if (newV && newV.length > 0) {
          this.initializeIndicator();
          this.handle_toc_scroll();
        }
      },
    },
  },
};
</script>

<style scoped>
.sb-navigation {
  max-height: calc(100vh - 90px);
  overflow-y: auto;
}

.custom-padding-left {
  padding-left: 15px;
  padding-right: 0px;
}

.eng-spacing.mt-3 {
  margin-top: 0.5rem !important;
}
</style>
